import { useState, useEffect } from "react";
import { getCachedImageUrl } from "./imageUtils";

/**
 * In-session blob URL cache.
 * Once an image is loaded (homepage card, previous Results visit, etc.),
 * its blob URL is stored here so any subsequent component that needs the same
 * URL gets it synchronously — no async re-lookup, no flicker.
 * Cleared on page reload (module re-initialisation).
 */
const _sessionCache = new Map();

/**
 * Returns a cached image src for an event hero image.
 * - Session cache (Map): synchronous, zero-delay for URLs already seen this session.
 * - Cache API: persistent across reloads — Firebase Storage URLs have no reliable
 *   cache-control headers so browser HTTP cache alone is insufficient.
 *
 * @param {string} eventId - The ID of the event
 * @param {string} remoteUrl - Firebase Storage URL from Firestore
 * @param {string} fallback - Fallback image path
 * @returns {string} - Image src to use in <img>
 */
export const useEventImage = (eventId, remoteUrl) => {
    const [src, setSrc] = useState(() =>
        remoteUrl ? (_sessionCache.get(remoteUrl) || remoteUrl) : ""
    );

    useEffect(() => {
        if (!remoteUrl) { setSrc(""); return; }

        if (_sessionCache.has(remoteUrl)) {
            setSrc(_sessionCache.get(remoteUrl));
            return;
        }

        let cancelled = false;
        getCachedImageUrl(eventId, remoteUrl).then((cachedSrc) => {
            if (!cancelled) {
                _sessionCache.set(remoteUrl, cachedSrc);
                setSrc(cachedSrc);
            }
        });

        return () => { cancelled = true; };
    }, [eventId, remoteUrl]);

    return src;
};
